import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Loader2, LayoutDashboard, Monitor, BarChart3, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAnalyticsStore } from "@/stores/AnalyticsStore";
import { KPICards } from "./KPICards";
import { TrendChart } from "./TrendChart";
import { UsageHeatmap } from "./UsageHeatmap";
import { ErrorAnalysis } from "./ErrorAnalysis";
import { PeakHoursCard } from "./PeakHoursCard";
import { SessionsAnalytics } from "./SessionsAnalytics";
import { SuccessRatePieChart } from "./SuccessRatePieChart";
import { ResponseTimeChart } from "./ResponseTimeChart";

type TabId = "overview" | "sessions" | "queries" | "errors";

function TabButton({
  active,
  onClick,
  children,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active
          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

export function AnalyticsSettings() {
  const { t } = useTranslation();
  const { data, sessionData, isLoading, isSessionLoading, error, selectedDays, fetchAnalytics, setSelectedDays } = useAnalyticsStore();
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRefresh = () => {
    fetchAnalytics(selectedDays);
  };

  const handleDaysChange = (value: string) => {
    setSelectedDays(parseInt(value, 10));
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">{t("analytics.loading", "Lade Analytics...")}</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button variant="outline" className="mt-2" onClick={handleRefresh}>
          {t("analytics.retry", "Erneut versuchen")}
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t("analytics.noData", "Keine Daten verfügbar")}
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("analytics.title", "Statistiken")}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("analytics.subtitle", "Umfassende Nutzungsstatistiken")}
            {" | "}{t("analytics.periodInfo", "Zeitraum")}: {data.period.start} - {data.period.end}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDays.toString()} onValueChange={handleDaysChange}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t("analytics.period.7days", "7 Tage")}</SelectItem>
              <SelectItem value="14">{t("analytics.period.14days", "14 Tage")}</SelectItem>
              <SelectItem value="30">{t("analytics.period.30days", "30 Tage")}</SelectItem>
              <SelectItem value="90">{t("analytics.period.90days", "90 Tage")}</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards (always visible) */}
      <KPICards
        broker={data.broker}
        claimStatus={data.claim_status}
        sessionData={sessionData}
        conversations={data.conversations}
        toolCalls={data.tool_calls}
        chatMessages={data.chat_messages}
      />

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
        <TabButton
          active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
          icon={<LayoutDashboard className="h-4 w-4" />}
        >
          {t("analytics.tabs.overview", "Übersicht")}
        </TabButton>
        <TabButton
          active={activeTab === "sessions"}
          onClick={() => setActiveTab("sessions")}
          icon={<Monitor className="h-4 w-4" />}
        >
          {t("analytics.tabs.sessions", "Sessions")}
        </TabButton>
        <TabButton
          active={activeTab === "queries"}
          onClick={() => setActiveTab("queries")}
          icon={<BarChart3 className="h-4 w-4" />}
        >
          {t("analytics.tabs.queries", "Berater & Schadenstatus")}
        </TabButton>
        <TabButton
          active={activeTab === "errors"}
          onClick={() => setActiveTab("errors")}
          icon={<AlertTriangle className="h-4 w-4" />}
        >
          {t("analytics.tabs.errors", "Fehleranalyse")}
        </TabButton>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          {/* Trend + Peak Hours */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <TrendChart brokerData={data.broker.by_day} claimData={data.claim_status.by_day} />
            </div>
            <div className="xl:col-span-1">
              <PeakHoursCard
                brokerPeakHours={data.broker.peak_hours}
                claimPeakHours={data.claim_status.peak_hours}
                brokerBlacklisted={data.broker.blacklisted}
                brokerTotal={data.broker.total}
              />
            </div>
          </div>

          {/* Response Time + Success Pie */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ResponseTimeChart
              brokerData={data.broker.response_time_by_day ?? []}
              claimData={data.claim_status.response_time_by_day ?? []}
            />
            <SuccessRatePieChart broker={data.broker} claimStatus={data.claim_status} />
          </div>

          {/* Heatmap */}
          <UsageHeatmap
            brokerByHour={data.broker.by_hour}
            brokerByWeekday={data.broker.by_weekday}
            claimByHour={data.claim_status.by_hour}
            claimByWeekday={data.claim_status.by_weekday}
          />
        </div>
      )}

      {activeTab === "sessions" && (
        isSessionLoading && !sessionData ? (
          <div className="flex items-center justify-center h-48 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            {t("analytics.loading", "Lade Analytics...")}
          </div>
        ) : sessionData ? (
          <SessionsAnalytics data={sessionData} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t("analytics.sessions.noData", "Keine Session-Daten verfügbar")}
          </div>
        )
      )}

      {activeTab === "queries" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <TrendChart brokerData={data.broker.by_day} claimData={data.claim_status.by_day} />
            </div>
            <div className="xl:col-span-1">
              <PeakHoursCard
                brokerPeakHours={data.broker.peak_hours}
                claimPeakHours={data.claim_status.peak_hours}
                brokerBlacklisted={data.broker.blacklisted}
                brokerTotal={data.broker.total}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ResponseTimeChart
              brokerData={data.broker.response_time_by_day ?? []}
              claimData={data.claim_status.response_time_by_day ?? []}
            />
            <SuccessRatePieChart broker={data.broker} claimStatus={data.claim_status} />
          </div>

          <UsageHeatmap
            brokerByHour={data.broker.by_hour}
            brokerByWeekday={data.broker.by_weekday}
            claimByHour={data.claim_status.by_hour}
            claimByWeekday={data.claim_status.by_weekday}
          />
        </div>
      )}

      {activeTab === "errors" && (
        <div className="space-y-4">
          <ErrorAnalysis
            brokerErrors={data.broker.error_types}
            claimErrors={data.claim_status.error_types}
            days={selectedDays}
          />
          <SuccessRatePieChart broker={data.broker} claimStatus={data.claim_status} />
        </div>
      )}
    </div>
  );
}
